/**
 * What an employee's own claims add up to.
 *
 * The four figures the counter band states, derived from the claims themselves
 * so they cannot drift from the rows underneath.
 *
 * The book's fourth counter is "typical wait, from approval to payment". A
 * claim here records no payment date — only that it reached `reimbursed` — so
 * that number cannot be computed without inventing it. What replaces it is the
 * question it was standing in for anyway: money that has been approved and has
 * not arrived yet.
 */

export const CLAIM_YEAR_FIELD = ["createdDate", "createdAt"];

/** When the claim was raised, whichever field carries it. */
export const claimDate = (claim) => {
  for (const key of CLAIM_YEAR_FIELD) {
    const value = claim?.[key];
    if (!value) continue;
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const yearOf = (claim) => claimDate(claim)?.getFullYear() ?? null;

const sum = (claims) => claims.reduce((t, c) => t + (Number(c?.amount) || 0), 0);

const bucket = (claims, status) => {
  const rows = claims.filter((c) => c?.status === status);
  return { count: rows.length, amount: sum(rows), claims: rows };
};

/**
 * The band's four figures.
 *
 * Pending and approved-unpaid are deliberately *not* scoped to a year: money
 * still owed is owed whenever it was claimed, and a December claim still
 * waiting in January would vanish from a year-scoped counter at exactly the
 * moment it most needs chasing. Reimbursed and rejected are year-scoped,
 * because those are a record of what happened rather than an outstanding
 * obligation.
 */
export const claimTotals = (claims = [], now = new Date()) => {
  const all = (claims || []).filter(Boolean);
  const year = now.getFullYear();
  const thisYear = all.filter((c) => yearOf(c) === year);

  return {
    year,
    pending: bucket(all, "pending"),
    // approved but not yet paid out — the reason `reimbursed` is a status of
    // its own rather than a flag on an approved claim
    awaiting: bucket(all, "approved"),
    reimbursed: bucket(thisYear, "reimbursed"),
    rejected: bucket(thisYear, "rejected"),
  };
};

/** "3 claims" / "1 claim" / "none". */
export const claimCountLabel = (n, noneText = "none") =>
  n ? `${n} claim${n === 1 ? "" : "s"}` : noneText;

/**
 * The total under a filtered list.
 *
 * Totals what is on screen, not the whole history — a footer under a list
 * filtered to "rejected" that quietly reported every claim ever made would be
 * answering a question nobody asked.
 */
export const listedTotal = (claims = []) => ({
  count: (claims || []).length,
  amount: sum((claims || []).filter(Boolean)),
});
